import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';

export interface GradeEntry {
  grade: string;
  quantity_kg: number;
  notes: string;
}

interface GradeBreakdownInputProps {
  gradeBreakdown: GradeEntry[];
  onChange: (breakdown: GradeEntry[]) => void;
  expectedQuantity?: number;
}

const GRADE_OPTIONS = [
  { value: 'A', label: 'Grade A (Premium)' },
  { value: 'B', label: 'Grade B (Good)' },
  { value: 'C', label: 'Grade C (Average)' },
  { value: 'Small Size', label: 'Small Size' },
  { value: 'Damaged', label: 'Damaged' },
  { value: 'Other', label: 'Other' },
];

const GradeBreakdownInput: React.FC<GradeBreakdownInputProps> = ({
  gradeBreakdown,
  onChange,
  expectedQuantity
}) => {
  const totalQuantity = gradeBreakdown.reduce((sum, entry) => sum + (entry.quantity_kg || 0), 0);
  const difference = expectedQuantity ? expectedQuantity - totalQuantity : 0;

  const addEntry = () => {
    onChange([...gradeBreakdown, { grade: '', quantity_kg: 0, notes: '' }]);
  };

  const removeEntry = (index: number) => {
    onChange(gradeBreakdown.filter((_, i) => i !== index));
  };

  const updateEntry = (index: number, field: keyof GradeEntry, value: string | number) => {
    const updated = gradeBreakdown.map((entry, i) => {
      if (i === index) {
        return { ...entry, [field]: value };
      }
      return entry;
    });
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Grade Breakdown (KG)</Label>
        <Button type="button" variant="outline" size="sm" onClick={addEntry}>
          <Plus className="h-4 w-4 mr-1" />
          Add Grade
        </Button>
      </div>

      {gradeBreakdown.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-md">
          No grades added. Click "Add Grade" to start recording grade breakdown.
        </p>
      )}

      {gradeBreakdown.map((entry, index) => (
        <div key={index} className="flex gap-2 items-start p-3 bg-muted/30 rounded-md">
          <div className="flex-1 min-w-[120px]">
            <Select
              value={entry.grade}
              onValueChange={(value) => updateEntry(index, 'grade', value)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent>
                {GRADE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-24">
            <Input
              type="number"
              min="0"
              step="0.1"
              placeholder="KG"
              value={entry.quantity_kg || ''}
              onChange={(e) => updateEntry(index, 'quantity_kg', parseFloat(e.target.value) || 0)}
              className="h-9"
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder="Notes (optional)"
              value={entry.notes}
              onChange={(e) => updateEntry(index, 'notes', e.target.value)}
              className="h-9"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-destructive hover:text-destructive"
            onClick={() => removeEntry(index)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {gradeBreakdown.length > 0 && (
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-sm font-medium">Total:</span>
          <div className="text-right">
            <span className="font-semibold">{totalQuantity.toFixed(1)} KG</span>
            {expectedQuantity && (
              <span className={`ml-2 text-sm ${Math.abs(difference) > 1 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                {difference > 0 ? `(${difference.toFixed(1)} KG remaining)` : 
                 difference < 0 ? `(${Math.abs(difference).toFixed(1)} KG over)` : 
                 '(matches expected)'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GradeBreakdownInput;
